import React, { useContext } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PeopleDetails, SongArt } from 'interfaces';

import { LanguageContext } from 'context/languageContext';

import { getAlbum, getSong } from 'utils';

import SongsList from 'components/SongsList';
import Title from 'components/ui/Title';
import ArrowLink from 'components/ui/ArrowLink';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';

import {
  Wrapper,
  TitleExtra,
  PersonDetailsWrapper,
  PhotoBox,
  SongArtWrapper
} from './People.styles';

export interface PeopleProps {
  className?: string;
  songArt: SongArt;
  peopleDetails: PeopleDetails;
}

const getCorrectTag = (
  peopleDetails: PeopleDetails, descriptionId: string, locale: AppLocale, intl: IntlShape
) => {
  const kind = descriptionId.match(/\D+|\d+/g)[0];

  const variables = {
    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
    fulName: locale === AppLocale.EN
      ? `${peopleDetails?.fullFirstName} ${peopleDetails?.fullSurname}`
      : `${peopleDetails?.kanjiSurname}${peopleDetails?.kanjiFirstName}`,
    boldFullName: <b>
      {locale === AppLocale.EN
        ? `${peopleDetails?.fullFirstName} ${peopleDetails?.fullSurname}`
        : `${peopleDetails?.kanjiSurname}${peopleDetails?.kanjiFirstName}`}
    </b>,
    name: intl.formatMessage({ id: `people.${peopleDetails?.id}` }),
    boldName: <b>{intl.formatMessage({ id: `people.${peopleDetails?.id}` })}</b>,
    toruFullName: <Link to={`/about/toru`}>{intl.formatMessage({ id: 'people.toruFullName' })}</Link>,
    tomoya: <Link to={`/about/tomoya`}>{intl.formatMessage({ id: 'people.tomoya' })}</Link>,
    songNobodysHome: <Link to={getSong('nobodys-home', locale, 'link')}>{getSong('nobodys-home', locale, 'name')}</Link>,
    songNothingHelps: <Link to={getSong('nothing-helps', locale, 'link')}>{getSong('nothing-helps', locale, 'name')}</Link>,
    songDelusionAll: <Link to={getSong('delusion-all', locale, 'link')}>{getSong('delusion-all', locale, 'name')}</Link>,
    songWeAre: <Link to={getSong('we-are', locale, 'link')}>{getSong('we-are', locale, 'name')}</Link>,
    songDeeperDeeper: <Link to={getSong('deeper-deeper', locale, 'link')}>{getSong('deeper-deeper', locale, 'name')}</Link>,
    songTakeWhatYouWant: <Link to={getSong('take-what-you-want', locale, 'link')}>{getSong('take-what-you-want', locale, 'name')}</Link>,
    albumNicheSyndrome: <Link to={getAlbum('niche-syndrome', false, locale, 'link')}>{getAlbum('niche-syndrome', false, locale, 'name')}</Link>,
    albumDetox: <Link to={getAlbum('detox', false, locale, 'link')}>{getAlbum('detox', false, locale, 'name')}</Link>,
    albumKanjoEffect: <Link to={getAlbum('kanjo-effect', false, locale, 'link')}>{getAlbum('kanjo-effect', false, locale, 'name')}</Link>,
    albumJinseiKaketeBokuWa: <Link to={getAlbum('jinsei-kakete-boku-wa', false, locale, 'link')}>{getAlbum('jinsei-kakete-boku-wa', false, locale, 'name')}</Link>,
    albumAmbitions: <Link to={getAlbum('ambitions', false, locale, 'link')}>{getAlbum('ambitions', false, locale, 'name')}</Link>
  };

  switch (kind) {
    case 'smallTitle':
    case 'title': {
      return (
        <Title size={kind === 'smallTitle' ? 'normal' : 'big'} color={kind === 'smallTitle' ? 'black' : 'primary'} key={descriptionId}>
          {intl.formatMessage({ id: `people.${peopleDetails?.id}.${descriptionId}` }, { ...variables })}
        </Title>
      );
    }
    case 'photo': {
      return (
        <img key={descriptionId} src={`/photos/${descriptionId}.jpg`} alt="" />
      );
    }
    default: {
      return (
        <p key={descriptionId}>
          {intl.formatMessage({ id: `people.${peopleDetails?.id}.${descriptionId}` }, { ...variables })}
        </p>
      );
    }
  }
}

const People = ({ peopleDetails, songArt, className = '' }: PeopleProps) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  return (
    <Wrapper className={className}>
      {
        peopleDetails.isMember ? (
          <Title size="normal" color="primary" noMargin>
            {intl.formatMessage({ id: 'members.title' }, {
              bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
              memberProfession: <TitleExtra>{intl.formatMessage({ id: `members.profession.${peopleDetails.memberType}` })}</TitleExtra>
            })}
          </Title>
        ) : null
      }
      <Title size="big" color="secondary">
        {peopleDetails?.name}
      </Title>
      {
        peopleDetails.isMember ? (
          <PersonDetailsWrapper>
            <PhotoBox>
              <img src={`/photos/${peopleDetails?.id}.jpg`} alt="" />
            </PhotoBox>
            <table>
              <tbody>
              {
                locale === AppLocale.EN && (peopleDetails?.fullSurname || peopleDetails?.fullFirstName) ? (
                  <tr>
                    <td>{intl.formatMessage({ id: `people.nameTitle` })}</td>
                    <td>{peopleDetails?.fullSurname} {peopleDetails?.fullFirstName}</td>
                  </tr>
                ) : null
              }
              {
                peopleDetails?.kanjiSurname || peopleDetails?.kanjiFirstName ? (
                  <tr>
                    <td>{intl.formatMessage({ id: `people.kanjiNameTitle` })}</td>
                    <td>
                      {peopleDetails?.kanjiSurname}{peopleDetails?.kanjiFirstName}
                      {
                        peopleDetails?.kanaSurname || peopleDetails?.kanaFirstName
                          ? ` (${peopleDetails?.kanaSurname} ${peopleDetails?.kanaFirstName})`
                          : null
                      }
                    </td>
                  </tr>
                ) : null
              }
              {
                peopleDetails?.birthdate ? (
                  <tr>
                    <td>{intl.formatMessage({ id: `people.birthTitle` })}</td>
                    <td>
                      {intl.formatDate(peopleDetails?.birthdate, {
                        year: 'numeric', month: 'short', day: 'numeric'
                      })}
                    </td>
                  </tr>
                ) : null
              }
              {
                peopleDetails?.placeOfBirthCityId && peopleDetails?.placeOfBirthCountryId ? (
                  <tr>
                    <td>{intl.formatMessage({ id: `people.BirthplaceTitle` })}</td>
                    <td>
                      {intl.formatMessage({ id: 'people.birthplace' }, {
                        city: intl.formatMessage({ id: `concert.city.${peopleDetails?.placeOfBirthCityId}` }),
                        country: intl.formatMessage({ id: `concert.country.${peopleDetails?.placeOfBirthCountryId}` })
                      })}
                    </td>
                  </tr>
                ) : null
              }
              </tbody>
            </table>
          </PersonDetailsWrapper>
        ) : null
      }
      {
        peopleDetails?.description?.length ? (
          peopleDetails?.description.map((descriptionId: string) =>
            getCorrectTag(peopleDetails, descriptionId, locale, intl))
        ) : <p>{intl.formatMessage({ id: 'people.noDescription' })}</p>
      }
      {
        peopleDetails.isMember ? (
          <React.Fragment>
            <ArrowLink size="normal" color="secondary" to="/about">
              {intl.formatMessage({ id: 'members.readMore' }, {
                bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
              })}
            </ArrowLink>
          </React.Fragment>
        ) : null
      }
      {
        songArt.lyrics?.length || songArt.arrangement?.length || songArt.music?.length || songArt.production?.length ? (
          <React.Fragment>
            <hr />
            <Title element="h1" size="big" color="secondary">{intl.formatMessage({ id : 'people.songArt' })}</Title>
            {
              songArt.lyrics?.length ? (
                <SongArtWrapper>
                  <Title size="big" color="primary">
                    {intl.formatMessage({ id: 'people.songArt.lyricsTitle' })}
                  </Title>
                  <table>
                    <tbody>
                    <tr>
                      <td>{intl.formatMessage({ id: 'people.songArt.lyrics' })}</td>
                      <td>
                        <div>
                          <strong>{songArt.lyrics.length}</strong>
                          {intl.formatMessage({ id: `people.songArt.songs` }, { count: songArt.lyrics.length })}
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <SongsList songs={songArt.lyrics} listType="concert" />
                </SongArtWrapper>
              ) : null
            }
            {
              songArt.music?.length ? (
                <SongArtWrapper>
                  <Title size="big" color="primary">
                    {intl.formatMessage({ id: 'people.songArt.musicTitle' })}
                  </Title>
                  <table>
                    <tbody>
                    <tr>
                      <td>{intl.formatMessage({ id: 'people.songArt.music' })}</td>
                      <td>
                        <div>
                          <strong>{songArt.music.length}</strong>
                          {intl.formatMessage({ id: `people.songArt.songs` }, { count: songArt.music.length })}
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <SongsList songs={songArt.music} listType="concert" />
                </SongArtWrapper>
              ) : null
            }
            {
              songArt.arrangement?.length ? (
                <SongArtWrapper>
                  <Title size="big" color="primary">
                    {intl.formatMessage({ id: 'people.songArt.arrangementTitle' })}
                  </Title>
                  <table>
                    <tbody>
                    <tr>
                      <td>{intl.formatMessage({ id: 'people.songArt.arrangement' })}</td>
                      <td>
                        <div>
                          <strong>{songArt.arrangement.length}</strong>
                          {intl.formatMessage({ id: `people.songArt.songs` }, { count: songArt.arrangement.length })}
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <SongsList songs={songArt.arrangement} listType="concert" />
                </SongArtWrapper>
              ) : null
            }
            {
              songArt.production?.length ? (
                <SongArtWrapper>
                  <Title size="big" color="primary">
                    {intl.formatMessage({ id: 'people.songArt.productionTitle' })}
                  </Title>
                  <table>
                    <tbody>
                    <tr>
                      <td>{intl.formatMessage({ id: 'people.songArt.production' })}</td>
                      <td>
                        <div>
                          <strong>{songArt.production.length}</strong>
                          {intl.formatMessage({ id: `people.songArt.songs` }, { count: songArt.production.length })}
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <SongsList songs={songArt.production} listType="concert" />
                </SongArtWrapper>
              ) : null
            }
          </React.Fragment>
        ) : null
      }
    </Wrapper>
  );
};

export default People;
